import type { ColumnDef, ColumnOrderState, RowData } from '@tanstack/react-table';
import { isNil } from 'es-toolkit';

function getColumnId<TData extends RowData>(column: ColumnDef<TData>) {
  if (!isNil(column.id)) return column.id;
  if ('accessorKey' in column && !isNil(column.accessorKey)) {
    return String(column.accessorKey);
  }
  return null;
}

export function getDefaultColumnOrder<TData extends RowData>(columns: ColumnDef<TData>[]) {
  return columns.reduce<string[]>((order, column) => {
    const columnId = getColumnId(column);
    if (columnId !== null && !order.includes(columnId)) {
      order.push(columnId);
    }
    return order;
  }, []);
}

export function reconcileColumnOrder<TData extends RowData>(
  columnOrder: ColumnOrderState,
  columns: ColumnDef<TData>[],
) {
  const defaultOrder = getDefaultColumnOrder(columns);
  const validIds = new Set(defaultOrder);
  const deduped = columnOrder.filter((columnId) => validIds.has(columnId));
  const dedupedSet = new Set(deduped);
  const missing = defaultOrder.filter((columnId) => !dedupedSet.has(columnId));
  return [...deduped, ...missing];
}

export function reorderColumnsWithinSubset({
  columnOrder,
  movableColumnIds,
  activeId,
  overId,
}: {
  readonly columnOrder: ColumnOrderState;
  readonly movableColumnIds: readonly string[];
  readonly activeId: string;
  readonly overId: string;
}) {
  if (activeId === overId) return columnOrder;
  const oldIndex = movableColumnIds.indexOf(activeId);
  const newIndex = movableColumnIds.indexOf(overId);
  if (oldIndex === -1 || newIndex === -1) return columnOrder;

  const reorderedMovableColumnIds = [...movableColumnIds];
  const [moved] = reorderedMovableColumnIds.splice(oldIndex, 1);
  if (isNil(moved)) return columnOrder;
  reorderedMovableColumnIds.splice(newIndex, 0, moved);

  const reorderedMovableSet = new Set(reorderedMovableColumnIds);
  let reorderedIndex = 0;
  return columnOrder.map((columnId) => {
    if (reorderedMovableSet.has(columnId)) {
      const nextId = reorderedMovableColumnIds[reorderedIndex];
      if (isNil(nextId)) return columnId;
      reorderedIndex += 1;
      return nextId;
    }
    return columnId;
  });
}

export function isColumnOrderable(columnMeta: { columnOrdering?: { enabled?: boolean } } | undefined) {
  return columnMeta?.columnOrdering?.enabled !== false;
}
